import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import LocationCityIcon from '@mui/icons-material/LocationCity';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import CityRow from 'components/CityRow';
import FabDialog from 'components/FabDialog';
import AddCityDialog from 'components/AddCityDialog';

import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  RegionEditCitiesRefetchQuery,
} from '__generated__/RegionEditCitiesRefetchQuery.graphql';
import { RegionEditCities_region$key } from '__generated__/RegionEditCities_region.graphql';

type Props = {
  region: RegionEditCities_region$key;
};

const RegionEditCities: FC<Props> = props => {
  const { variables } = usePagination();

  const [region, citiesQuery] = useAvoidSuspense<
    RegionEditCitiesRefetchQuery,
    RegionEditCities_region$key
  >(
    graphql`
      fragment RegionEditCities_region on Region
      @refetchable(queryName: "RegionEditCitiesRefetchQuery") {
        cities(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id
              ...CityRow_city
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }

        ...AddCityDialog_region
      }
    `,
    props.region,
    Query
  );

  const connectionId = region.cities?.__id;

  const [deleteRegion, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = region.cities ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      citiesQuery.refetch({
        id: region.id,
        ...variables,
      });
    }, [region.id, citiesQuery, variables])
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Table
        pagination
        pageInfo={pageInfo ?? null}
        icon={<LocationCityIcon />}
        loading={citiesQuery.loading || deleting}
        head={
          <TableRow>
            <TableCell>Pavadinimas</TableCell>
            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <CityRow key={node.id} city={node} onDelete={deleteRegion} />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm" icon={<AddLocationIcon />}>
          <AddCityDialog connectionId={connectionId} region={region} />
        </FabDialog>
      )}
    </Paper>
  );
};

export default RegionEditCities;
